import { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from 'react-router-dom'
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonCard,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCol,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonPage,
  IonProgressBar,
  IonRow,
  IonTitle,
  IonToast,
  IonToolbar,
  IonMenuButton,
  IonFabButton,
  IonFab,
  IonFabList,
  IonModal,
  IonTextarea
} from "@ionic/react";
import {
  arrowRedoOutline,
  colorWandOutline,
  logoFacebook,
  logoTwitter,
  logoInstagram,
  logoWhatsapp,
  logoTiktok,
  copyOutline,
  globeOutline,
  businessOutline,
  checkmarkCircle,
  chatbubbleEllipsesOutline,
  createOutline
} from "ionicons/icons";
import { useParams } from "react-router";
import { getColors } from "../helpers/utils";
import { getOrganizationFromUrl } from "../helpers/registrationLinkUtils";
import styles from "./ViewPoll.module.scss";
import axios from "axios";
import { API_BASE_URL } from "../config/api";
import BackButton from "./BackButton"
import Loader from "../components/Loader";

const ViewPoll = () => {
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [poll, setPoll] = useState(null);
  const [colors, setColors] = useState({
    backgroundColor: "#ffffff",
    textColor: "#000000",
    subTextColor: "#666666",
    votedButtonStyle: { backgroundColor: "#00ff00" },
    notVotedbuttonStyle: { backgroundColor: "#cccccc" },
    percentTrack: { backgroundColor: "#ddd" },
  });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showVotes, setShowVotes] = useState(false);
  const [showShareText, setShowShareText] = useState(false);
  const [userVotes, setUserVotes] = useState([]);

  // Sleek Reason Modal State
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [reasonText1, setReasonText1] = useState("");
  const [reasonText2, setReasonText2] = useState("");


  const [pendingAnswerId, setPendingAnswerId] = useState(null);
  const [showEditReasonModal, setShowEditReasonModal] = useState(false);
  const [editingVote, setEditingVote] = useState(null);
  const [editReasonText, setEditReasonText] = useState("");
  const [updatingReason, setUpdatingReason] = useState(false);

  useEffect(() => {
    const userId = sessionStorage.getItem("user_id");
    if (!userId) {
      // Not logged in, redirect to register with return path
      history.replace(`/page/register?redirect=/page/view/${id}`);
    }
  }, [id, history]);

  const answerRefs = useRef([]);

  const calculatePollDuration = (timeLeft) => {
    if (!timeLeft) return 0;

    const timeParts = timeLeft.split(", ").map((part) => part.trim());
    let totalMilliseconds = 0;

    timeParts.forEach((part) => {
      if (part.includes("days")) {
        totalMilliseconds += parseInt(part) * 24 * 60 * 60 * 1000;
      } else if (part.includes("hours")) {
        totalMilliseconds += parseInt(part) * 60 * 60 * 1000;
      } else if (part.includes("mins")) {
        totalMilliseconds += parseInt(part) * 60 * 1000;
      }
    });

    return totalMilliseconds;
  };

  const formatTimeRemaining = (milliseconds) => {
    const days = Math.floor(milliseconds / (24 * 60 * 60 * 1000));
    const hours = Math.floor((milliseconds % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((milliseconds % (60 * 60 * 1000)) / (60 * 1000));

    return `${days} days, ${hours} hours, ${minutes} mins`;
  };

  const fetchPollData = useCallback(async () => {
    setLoading(true);
    const safetyTimeout = setTimeout(() => {
      setLoading(false);
    }, 12000);

    try {
      const userId = sessionStorage.getItem("user_id");
      const response = await axios.get(`${API_BASE_URL}/get_poll_simple.php?id=${id}&userId=${userId}`, { timeout: 8000 });
      const data = response.data.poll;

      const currentTime = new Date();
      const pollEndTime = new Date(
        new Date(data.created_at).getTime() + calculatePollDuration(data.time_left)
      );
      const timeRemaining = pollEndTime - currentTime;

      data.time_left = timeRemaining > 0 ? formatTimeRemaining(timeRemaining) : "Poll expired";
      data.expired = timeRemaining <= 0;

      setPoll(data);

      if (data.color) {
        const cardStyleColors = getColors(data.color);
        setColors(cardStyleColors);
      }

      // Set user votes from the fetched data
      setUserVotes(data.user_votes || []);
      const maxVotes = parseInt(data.max_votes_per_user || 1);
      setShowVotes(data.user_vote_count >= maxVotes);

    } catch (error) {
      console.error("Error fetching poll data:", error);
    } finally {
      clearTimeout(safetyTimeout);
      setLoading(false);
    }
  }, [id]);

  const updateAnswerVotes = async (pollId, answerId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/get_poll_simple.php?id=${pollId}`);
      const pollData = response.data.poll;

      // Increment the votes for the selected answer
      const updatedAnswers = pollData.answers.map((answer) => {
        if (answer.id === answerId) {
          answer.votes += 1;
        }
        return answer;
      });

      // Calculate the total votes for all answers
      const totalVotes = updatedAnswers.reduce((sum, answer) => sum + answer.votes, 0);

      // Calculate the percentage for each answer
      updatedAnswers.forEach((answer) => {
        answer.percent = Math.round((answer.votes / totalVotes) * 100);
      });

      // Update the poll data with the new answers
      await axios.post(`${API_BASE_URL}/update_poll_answers.php`, {
        pollId,
        answers: updatedAnswers,
        totalVotes
      });

      // Update the state with the new poll data
      setPoll((prevPoll) => ({
        ...prevPoll,
        answers: updatedAnswers,
        totalvotes: totalVotes
      }));
    } catch (error) {
      console.error("Error updating answer votes:", error);
    }
  };

  const addVote = useCallback(async (pollId, answerId, reason = null) => {
    try {
      const voteData = {
        userId: +sessionStorage.getItem("user_id"),
        pollId: pollId,
        answerId: answerId,
        ...(reason && { reason })
      };

      const response = await fetch(`${API_BASE_URL}/vote.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(voteData),
      });

      const result = await response.json();

      if (result.success) {
        setToastMessage("Vote recorded successfully!");
        setShowToast(true);
        // Update answer votes and fetch updated poll data
        await fetchPollData();
      } else if (result.max_votes_reached) {
        setToastMessage("You have voted for the maximum of 2 options!");
        setShowToast(true);
        setShowVotes(true); // Show results since they've maxed out votes
      } else if (result.already_voted_this_option) {
        setToastMessage("You already voted for this option. Choose a different one!");
        setShowToast(true);
      } else {
        throw new Error(result.error || "Vote submission failed");
      }
    } catch (error) {
      console.error("Error adding vote:", error);
      setToastMessage("Error adding vote: " + error.message);
      setShowToast(true);
    }
  }, [fetchPollData]);


  /*  const vote = async (answerId) => {
       if (userVotes.length < 2) {
         const updatedVotes = [...userVotes, answerId];
         setUserVotes(updatedVotes);
         await addVote(id, answerId);
     
         // Check if maximum votes reached
         if (updatedVotes.length === 2) {
           setToastMessage("Success: You have now voted for the maximum of 2 options.");
           setShowToast(true);
         }
         setTimeout(() => {
           // Explicitly set showVotes to true after updating userVotes
           setShowVotes(true);
         }, [2000])     
 
       } else {
         setToastMessage("Error: You can only vote for at most 2 options.");
         setShowToast(true);
       }
     }; */
  const submitVoteWithReason = async () => {
    const maxVotes = parseInt(poll?.max_votes_per_user || 1);
    if (!pendingAnswerId) return;

    setShowReasonModal(false);
    
    try {
      if (maxVotes === 2 && userVotes.length === 1) {
        // Double vote logic (existing logic updated)
        const firstVote = userVotes[0];
        const firstAnswerId = firstVote.answer_id || firstVote;

        const newUserVotes = [
          { answer_id: firstAnswerId, reason: reasonText1 },
          { answer_id: pendingAnswerId, reason: reasonText2 }
        ];

        setUserVotes(newUserVotes);
        setShowVotes(true);
        setToastMessage("Success: You have now voted for the maximum of 2 options.");
        setShowToast(true);

        // Update first vote
        await axios.post(`${API_BASE_URL}/update_vote_reason.php`, {
          userId: sessionStorage.getItem("user_id"),
          pollId: id,
          answerId: firstAnswerId,
          reason: reasonText1
        });

        // Submit second vote
        await addVote(id, pendingAnswerId, reasonText2);
      } else {
        // Single vote logic (or fallback for 1st vote in any multi-vote scenario that triggers reasons early)
        const newUserVotes = [{ answer_id: pendingAnswerId, reason: reasonText1 }];
        
        setUserVotes(newUserVotes);
        setShowVotes(true);
        setToastMessage(`Success: Your vote has been recorded.`);
        setShowToast(true);

        await addVote(id, pendingAnswerId, reasonText1);
      }
      
      // Refresh to ensure everything is synced
      await fetchPollData();
    } catch (error) {
      console.error("Error during reason submission:", error);
      setToastMessage("Warning: Your vote was recorded but the reason might not have saved correctly.");
      setShowToast(true);
    }

    // Reset modal state
    setReasonText1("");
    setReasonText2("");
    setPendingAnswerId(null);
  };



  const vote = useCallback(async (answerId) => {
    const maxVotes = parseInt(poll?.max_votes_per_user || 1);
    if (userVotes.length < maxVotes) {
      // Trigger sleek reason modal on the user's LAST allowed vote
      if (userVotes.length === (maxVotes - 1)) {
        setPendingAnswerId(answerId);
        // Pre-fill reason for the first option if it already exists
        setReasonText1(userVotes[0]?.reason || "");
        setReasonText2("");
        setShowReasonModal(true);
        return;
      }

      // 1. Create new votes array first
      const newUserVotes = [...userVotes, { answer_id: answerId }];

      // 2. Update votes state immediately
      setUserVotes(newUserVotes);

      // 3. Check length and update showVotes based on the new array
      if (newUserVotes.length === maxVotes) {
        setShowVotes(true);
        setToastMessage(`Success: You have now voted for the maximum of ${maxVotes} options.`);
        setShowToast(true);
      }

      // 4. Make API calls after state updates
      await addVote(id, answerId);
      await fetchPollData();
    }
  }, [userVotes, id, addVote, fetchPollData, poll]);


  const updateVoteReason = async () => {
    if (!editingVote || !editReasonText.trim()) return;

    setUpdatingReason(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/update_vote_reason.php`, {
        userId: sessionStorage.getItem("user_id"),
        pollId: id,
        answerId: editingVote.answer_id,
        reason: editReasonText
      });

      if (response.data.success) {
        setToastMessage("Reason updated successfully!");
        setShowToast(true);
        setShowEditReasonModal(false);
        await fetchPollData();
      } else {
        throw new Error(response.data.error || "Update failed");
      }
    } catch (error) {
      console.error("Error updating reason:", error);
      setToastMessage("Error: " + error.message);
      setShowToast(true);
    } finally {
      setUpdatingReason(false);
    }
  };

  const openEditReasonModal = () => {
    setShowEditReasonModal(true);
  };


  const getAnswerPercentage = (totalVotes, answerVotes) => {
    if (!totalVotes || totalVotes === 0) return 0;
    return Math.round((answerVotes / totalVotes) * 100);
  };

  useEffect(() => {
    if (!sessionStorage.getItem("user_id")) {
      // Check if there is an organization code in the URL
      const orgInfo = getOrganizationFromUrl();
      if (orgInfo) {
        // If org code exists, redirect to registration with the current query params
        history.replace("/page/register" + window.location.search);
      } else {
        history.replace("/page/login");
      }
      return;
    }

    fetchPollData();
  }, [fetchPollData, history]);


  return (
    <IonPage className="view-poll-page">
      <IonHeader className="ion-no-border" translucent={true}>
        <IonToolbar className="header-toolbar frosted-header">
          <IonButtons slot="start">
            <IonBackButton text="Back" style={{ color: poll?.color || 'var(--ion-color-primary)' }} />
          </IonButtons>
          <IonButtons slot="end">
            {poll && (
              <div className={`${styles.pollTypeBadgeHeader} ${(poll.visibility === 'public' || poll.creator_role_id === 1 || poll.creator_role_id === '1')
                ? styles.general
                : styles.organization
                }`}>
                <IonIcon icon={(poll.visibility === 'public' || poll.creator_role_id === 1 || poll.creator_role_id === '1') ? globeOutline : businessOutline} />
                <span>{(poll.visibility === 'public' || poll.creator_role_id === 1 || poll.creator_role_id === '1') ? "General" : "Org"} Poll</span>
              </div>
            )}
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Peoppoll Votes</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="view-poll-content">
        {loading && <Loader message="Analyzing Community Sentiment..." />}
        <IonHeader collapse="condense">
          <IonToolbar>
            <IonTitle size="large">People Opinion Polls</IonTitle>
          </IonToolbar>
        </IonHeader>

        <div className="reduceWidth">
          {poll && (
            <div className="poll-content-card">
              <IonCard
                className={`${styles.pollQuestion} animate__animated animate__fadeIn`}
                style={{ backgroundColor: 'transparent', background: 'transparent', boxShadow: 'none', margin: 0, border: 'none' }}
              >
                <IonCardHeader className="ion-text-left">
                  <IonCardTitle style={{ color: colors.textColor }}>
                    {poll.question}
                  </IonCardTitle>
                  <IonCardSubtitle
                    className="ion-margin-bottom"
                    style={{ color: colors.subTextColor }}
                  >
                    {poll.time_left} left
                  </IonCardSubtitle>
                  <p style={{ color: colors.textColor, margin: "0" }}>
                    {poll.totalvotes} votes already
                  </p>
                </IonCardHeader>
              </IonCard>

              <div className="animate__animated animate__fadeIn">
                {poll.answers.map((answer, index) => {
                  // Recalculate total votes to ensure consistency and avoid division by zero
                  const calculatedTotalVotes = poll.answers.reduce((sum, a) => sum + a.votes, 0);

                  const answerPercentage = getAnswerPercentage(
                    calculatedTotalVotes,
                    answer.votes
                  );

                  const isVoted = userVotes.some(v => (v.answer_id == answer.id || v == answer.id)) || answer.voted;

                  return (
                    <IonRow
                      key={`answer_${answer.id}`}
                      className={`ion-align-items-center ion-justify-content-start ${styles.pollAnswer}`}
                    >
                      <IonCol size="11">
                        {poll.user_vote_count >= 2 || showVotes ? (
                          <div id={`answerVoted_${answer.id}`}>
                            <p style={{ color: poll.color }}>
                              {answer.answer} ({answerPercentage}%)
                            </p>
                            {showVotes && (
                              <p style={{ color: poll.color }}>
                                {answer.votes} of {calculatedTotalVotes} total votes
                              </p>
                            )}
                            <IonProgressBar
                              value={answerPercentage / 100}
                              style={colors.percentTrack}
                            />
                          </div>
                        ) : (
                          <IonButton
                            className="animate__animated"
                            ref={(ref) => (answerRefs.current[index] = ref)}
                            expand="block"
                            fill={isVoted ? "solid" : "outline"}
                            disabled={isVoted}
                            style={{
                              ...(isVoted
                                ? colors.votedButtonStyle
                                : colors.notVotedbuttonStyle),
                              '--justify-content': 'flex-start',
                              '--padding-start': '1.5rem',
                              textTransform: 'none',
                              opacity: isVoted ? 0.7 : 1, // Slight grayed out effect
                            }}
                            onClick={() => vote(answer.id)}
                          >
                            <div className="flex items-center gap-2">
                              {isVoted && <IonIcon icon={checkmarkCircle} slot="start" style={{ marginRight: '8px' }} />}
                              <span>{answer.answer}</span>
                              {poll.voted && ` (${answerPercentage}%)`}
                            </div>
                          </IonButton>
                        )}
                      </IonCol>
                    </IonRow>
                  );
                })}
              </div>
            </div>
          )}
        </div>

      </IonContent>
      <IonToast
        duration={2000}
        header="Poll"
        message={toastMessage}
        onDidDismiss={() => setShowToast(false)}
        isOpen={showToast && !!toastMessage}
        color={toastMessage.includes("Error") || toastMessage.includes("maximum") || toastMessage.includes("already voted") ? "danger" : "success"}
        position="bottom"
      />


      <IonFooter>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem',
          gap: '1rem'
        }}>
          {/* Left side: Back button and Share button */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <BackButton bgKolor={colors.backgroundColor} />

            {/* FAB for Sharing */}
            <IonFab vertical="left" horizontal="left" title="Share this poll">
              <IonFabButton
                style={{
                  backgroundColor: colors.votedButtonStyle.backgroundColor,
                  borderRadius: "50%",
                  padding: "8px",
                  width: "48px",
                  height: "48px",
                  position: "relative",
                  left: "100px"
                }}
                onClick={() => setShowShareText((prev) => !prev)}
              >
                <IonIcon icon={arrowRedoOutline} />
              </IonFabButton>
              {showShareText && (
                <div
                  className="animate__animated animate__fadeIn"
                  style={{
                    position: 'absolute',
                    bottom: '60px',
                    left: '20px',
                    zIndex: 1000,
                    minWidth: '220px',
                    background: 'rgba(255, 255, 255, 0.75)',
                    backdropFilter: 'blur(16px)',
                    WebkitBackdropFilter: 'blur(16px)',
                    border: '1px solid rgba(255, 255, 255, 0.8)',
                    borderRadius: '20px',
                    padding: '16px',
                    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px'
                  }}
                >
                  {/* Share Message Generation */}
                  {(() => {
                    const orgInfo = getOrganizationFromUrl();
                    let shareUrl = window.location.href;

                    // Ensure Organization Code is appended if available
                    if (orgInfo && orgInfo.registrationCode) {
                      try {
                        const urlObj = new URL(window.location.href);
                        // Set 'm' parameter for organization code
                        urlObj.searchParams.set('m', orgInfo.registrationCode);
                        // Also keep 'org' if it was there, or standardise on 'm'
                        shareUrl = urlObj.toString();
                      } catch (e) {
                        console.error("Error constructing share URL", e);
                      }
                    }

                    const shareText = `Check out this poll on Peoppoll: "${poll?.question || ''}" - Vote now!`;

                    const handleCopyLink = () => {
                      navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
                      setToastMessage("Link & Message copied to clipboard!");
                      setShowToast(true);
                    };

                    const handleFacebookShare = () => {
                      // Facebook doesn't allow pre-filling via URL for security/spam reasons.
                      // We copy the text for the user so they can paste it easily.
                      const fullPost = `${shareText} ${shareUrl}`;
                      navigator.clipboard.writeText(fullPost);
                      setToastMessage("Text copied! Facebook requires you to manually paste into the box.");
                      setShowToast(true);

                      // Using the sharer with 'quote' (though deprecated, it sometimes helps in certain versions)
                      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`, "_blank");
                    };

                    const handleInstagramShare = () => {
                      // Copy message and open Instagram
                      navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
                      setToastMessage("Message & Link copied! Paste it on Instagram.");
                      setShowToast(true);
                      window.open("https://www.instagram.com/", "_blank");
                    };

                    return (
                      <>
                        {/* Facebook */}
                        <div className="flex items-center gap-3 cursor-pointer group"
                          onClick={handleFacebookShare}>
                          <div className="bg-[#1877F2] text-white p-2 rounded-full shadow-sm group-hover:scale-110 transition-transform">
                            <IonIcon icon={logoFacebook} />
                          </div>
                          <span className="text-sm font-semibold text-gray-700">Facebook</span>
                        </div>

                        {/* Twitter / X */}
                        <div className="flex items-center gap-3 cursor-pointer group"
                          onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank")}>
                          <div className="bg-[#000000] text-white p-2 rounded-full shadow-sm group-hover:scale-110 transition-transform">
                            <IonIcon icon={logoTwitter} />
                          </div>
                          <span className="text-sm font-semibold text-gray-700">Twitter (X)</span>
                        </div>

                        {/* WhatsApp */}
                        <div className="flex items-center gap-3 cursor-pointer group"
                          onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, "_blank")}>
                          <div className="bg-[#25D366] text-white p-2 rounded-full shadow-sm group-hover:scale-110 transition-transform">
                            <IonIcon icon={logoWhatsapp} />
                          </div>
                          <span className="text-sm font-semibold text-gray-700">WhatsApp</span>
                        </div>

                        {/* TikTok (Copy Link) */}
                        <div className="flex items-center gap-3 cursor-pointer group"
                          onClick={handleCopyLink}>
                          <div className="bg-[#000000] text-white p-2 rounded-full shadow-sm group-hover:scale-110 transition-transform">
                            <IonIcon icon={logoTiktok} />
                          </div>
                          <span className="text-sm font-semibold text-gray-700">TikTok (Copy)</span>
                        </div>

                        {/* Instagram */}
                        <div className="flex items-center gap-3 cursor-pointer group"
                          onClick={handleInstagramShare}>
                          <div className="bg-gradient-to-tr from-[#f09433] via-[#dc2743] to-[#bc1888] text-white p-2 rounded-full shadow-sm group-hover:scale-110 transition-transform">
                            <IonIcon icon={logoInstagram} />
                          </div>
                          <span className="text-sm font-semibold text-gray-700">Instagram</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </IonFab>
          </div>

          <IonButton
            fill="outline"
            style={{ ...colors.notVotedbuttonStyle, '--border-radius': '10px' }}
            onClick={openEditReasonModal}
            disabled={userVotes.length === 0}
          >
            <IonIcon icon={chatbubbleEllipsesOutline} />
            &nbsp; My Reason
          </IonButton>
          <IonButton
            fill="outline"
            style={{ ...colors.notVotedbuttonStyle, '--border-radius': '10px' }}
            onClick={() => setShowVotes((prev) => !prev)}
          >
            <IonIcon icon={colorWandOutline} />
            &nbsp; {showVotes ? "Hide" : "Show"} votes
          </IonButton>
        </div>
      </IonFooter>

      {/* Edit Reason Selection Modal */}
      <IonModal
        isOpen={showEditReasonModal}
        onDidDismiss={() => setShowEditReasonModal(false)}
        className="sleek-reason-modal"
        breakpoints={[0, 0.5, 0.9]}
        initialBreakpoint={0.85}
      >
        <IonHeader className="ion-no-border">
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={() => setShowEditReasonModal(false)} color="medium">Close</IonButton>
            </IonButtons>
            <IonTitle className="text-sm font-bold text-center">My Reasons</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding reasons-modal-content custom-scrollbar">
          <div style={{ paddingBottom: '20px' }}>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">My Voting Reasons 🤔</h2>
            <div className="bg-indigo-50 p-3 rounded-lg border-l-4 border-indigo-500 mb-4">
               <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">POLL QUESTION:</p>
               <h3 className="text-lg font-bold text-indigo-900 leading-tight">
                 {poll?.question}
               </h3>
            </div>
            <p className="text-gray-600 mb-6 text-sm">
              Below are the options you chose and the reasons you provided. You can update them at any time.
            </p>

            {userVotes.map((v, idx) => {
              const answerObj = poll?.answers.find(a => a.id == v.answer_id) || { answer: `Option ${idx + 1}` };
              return (
                <div key={`edit_reason_${idx}`} className="mb-6 p-4 rounded-xl" style={{ background: '#f1f5f9', border: '1px solid #e2e8f0' }}>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-bold text-indigo-600">{answerObj.answer}</span>
                    <IonButton
                      fill="clear"
                      size="small"
                      onClick={() => {
                        setEditingVote(v);
                        setEditReasonText(v.reason || "");
                      }}
                    >
                      <IonIcon icon={createOutline} slot="start" />
                      Edit
                    </IonButton>
                  </div>
                  <p className="text-gray-700 italic text-sm">
                    {v.reason ? `"${v.reason}"` : "No reason provided yet."}
                  </p>

                  {editingVote && editingVote.answer_id === v.answer_id && (
                    <div className="mt-4 animate__animated animate__fadeIn">
                      <IonTextarea
                        className="bg-white rounded-lg p-2 border border-indigo-200"
                        rows={3}
                        placeholder="Update your reason..."
                        value={editReasonText}
                        onIonChange={(e) => setEditReasonText(e.detail.value)}
                      />
                      <div className="flex justify-end gap-2 mt-2">
                        <IonButton color="medium" fill="clear" size="small" onClick={() => setEditingVote(null)}>Cancel</IonButton>
                        <IonButton
                          color="primary"
                          size="small"
                          onClick={updateVoteReason}
                          disabled={updatingReason || !editReasonText.trim()}
                        >
                          {updatingReason ? "Saving..." : "Save Change"}
                        </IonButton>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </IonContent>
      </IonModal>

      {/* Sleek Reason Modal with Two Options */}
      <IonModal
        isOpen={showReasonModal}
        onDidDismiss={() => {
          setShowReasonModal(false);
          setReasonText1("");
          setReasonText2("");
          setPendingAnswerId(null);
        }}
        className="sleek-reason-modal"
        breakpoints={[0, 0.98]}
        initialBreakpoint={0.98}

      >
        <IonHeader className="ion-no-border">
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={() => setShowReasonModal(false)} color="medium">
                Cancel
              </IonButton>
            </IonButtons>
            <IonTitle className="text-sm font-bold text-center">Your Reasons</IonTitle>
            <IonButtons slot="end">
              <IonButton
                onClick={submitVoteWithReason}
                disabled={!reasonText1.trim() || (poll?.max_votes_per_user == 2 && !reasonText2.trim())}
                style={{ color: '#6366f1', fontWeight: 'bold' }}
              >
                Send
              </IonButton>

            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent className="ion-padding custom-scrollbar">
          <div className="py-2">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Wait, tell us why! 🤔</h2>
            
            <div className="bg-indigo-50 p-4 rounded-xl mb-6 border-l-4 border-indigo-500 animate__animated animate__fadeIn">
               <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-1">REASONING FOR:</p>
               <h3 className="text-lg font-bold text-indigo-900 leading-tight">
                 {poll?.question}
               </h3>
            </div>

            <p className="text-gray-600 mb-6 text-sm">
              We would love to know the reason behind your choices to help us better understand community sentiment.
            </p>


            {/* Field for First Choice (Double Vote) or Only Choice (Single Vote) */}
            {((poll?.max_votes_per_user == 2 && userVotes.length > 0) || poll?.max_votes_per_user == 1) && (
              <div className="mb-6 animate__animated animate__fadeIn">
                <label className="block text-sm font-semibold text-indigo-700 mb-2 uppercase tracking-wider">
                  Reason for choosing: {poll?.max_votes_per_user == 1 
                    ? (poll?.answers.find(a => a.id == pendingAnswerId)?.answer || "Your Choice")
                    : (poll?.answers.find(a => (a.id == (userVotes[0]?.answer_id || userVotes[0])))?.answer || "First Option")
                  }
                </label>
                <IonTextarea
                  className="border-2 border-indigo-100 rounded-xl p-4 shadow-inner focus:border-indigo-400 transition-colors"
                  style={{ background: '#f8fafc' }}
                  rows={3}
                  placeholder="Tell us why you are choosing this option..."
                  value={reasonText1}
                  onIonChange={(e) => setReasonText1(e.detail.value)}
                />
              </div>
            )}

            {/* Field for Second Choice (Only for Double Vote) */}
            {poll?.max_votes_per_user == 2 && pendingAnswerId && (
              <div className="mb-6 animate__animated animate__fadeIn">
                <label className="block text-sm font-semibold text-indigo-700 mb-2 uppercase tracking-wider">
                  Reason for choosing: {poll?.answers.find(a => a.id == pendingAnswerId)?.answer || "Second Option"}
                </label>
                <IonTextarea
                  className="border-2 border-indigo-100 rounded-xl p-4 shadow-inner focus:border-indigo-400 transition-colors"
                  style={{ background: '#f8fafc' }}
                  rows={3}
                  placeholder="Tell us why you are choosing this option..."
                  value={reasonText2}
                  onIonChange={(e) => setReasonText2(e.detail.value)}
                />
              </div>
            )}

          </div>
        </IonContent>
      </IonModal>

    </IonPage>
  );
};
export default ViewPoll;